import React, { useState } from 'react';
import { useHawkSearch } from 'components/StoreProvider';
import { useHawkConfig } from 'components/ConfigProvider';
import Facet from './Facet';
import PlaceholderFacet from './PlaceholderFacet';
import { getFacetComponents } from 'components/ui/Facets/Overrides';
import Selections from './Selections';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import PlusSVG from '../../svg/PlusSVG';
import XSVG from '../../svg/XSVG';
import { Element } from 'react-scroll';
import useWindowSize from 'shared/hooks/useWindowSize';
import { ScrollToResults } from 'features/search/FacetsAndResults';

function FacetList(props: ScrollToResults) {
	const { scrollToResults } = props;

	const [isFacetsOpen, setIsFacetsOpen] = useState(false);
	const size = useWindowSize();

	const {
		store: { searchResults, facetSelections },
	} = useHawkSearch();

	const { config } = useHawkConfig();
	const { t } = useTranslation();

	// the number of random placeholders to render while we wait for results
	const [numPlaceholders] = useState(Math.round(Math.random() * (5 - 3) + 3));

	function handleMobileToggleClick() {
		if (isFacetsOpen) {
			closeFacets();
			return;
		}

		openFacets();
	}

	function openFacets() {
		setIsFacetsOpen(true);
		document.body.classList.add('hawk-facets-open');
	}

	function closeFacets() {
		setIsFacetsOpen(false);
		document.body.classList.remove('hawk-facets-open');
	}

	function onFacetChanged() {
		scrollToResults('facet');
	}

	const components = getFacetComponents(config.facetOverrides || []);

	if (!searchResults) {
		return (
			<div className="hawk-facet-rail hawk-facet-rail--placeholder">
				{[...Array(numPlaceholders)].map((_, i) => (
					<PlaceholderFacet key={i} />
				))}
			</div>
		);
	}

	const isNoSelections = Object.keys(facetSelections).length === 0;

	const isNoHawkFacets = searchResults.Facets.length === 0;

	if (searchResults.Results.length === 0 && (isNoSelections || isNoHawkFacets)) {
		return null;
	}

	const hawkFacetRailButtonIconClass = 'hawk-facet-rail__heading-icon';
	const isMobile = size && size.width && size.width < 768;

	return (
		<>
			<Element
				id="hawk-facet-rail__scroll-to-facets"
				name="hawk-facet-rail__scroll-to-facets"
				className="hawk-facet-rail__scroll-to-facets"
			/>

			<div
				className={classnames('hawk-facet-rail', {
					'hawk-facet-rail--mobile-open': isMobile && isFacetsOpen,
				})}
			>
				{isMobile ? (
					<button
						onClick={handleMobileToggleClick}
						className={classnames('hawk-facet-rail__heading hawk-facet-rail__heading--btn', {
							'hawk-facet-rail__heading--closed': !isFacetsOpen,
							'hawk-facet-rail__heading--open': isFacetsOpen,
						})}
						aria-expanded={isFacetsOpen}
					>
						<span className="hawk-facet-rail__heading-text">{t('Narrow Results')}</span>

						{isFacetsOpen ? (
							<XSVG className={hawkFacetRailButtonIconClass} />
						) : (
							<PlusSVG class={hawkFacetRailButtonIconClass} />
						)}
					</button>
				) : (
					<div className="hawk-facet-rail__heading">{t('Narrow Results')}</div>
				)}
				{!isMobile || (isMobile && isFacetsOpen) ? (
					<div className="hawk-facet-rail__facet-container">
						<Selections onFacetChange={onFacetChanged} />

						{searchResults.Facets.map((facet) => {
							if (!facet.IsVisible || facet.FieldType === 'tab') {
								return null;
							}

							const registeredComponent = components.find(
								(component) => component.facetType === facet.FacetType,
							);
							const Component = !registeredComponent ? null : registeredComponent.component;

							return (
								<Facet key={facet.FacetId} facet={facet} onFacetChange={onFacetChanged}>
									{Component ? (
										<Component />
									) : (
										<div>
											{facet.FieldType} {facet.FacetType} is not implemented!
										</div>
									)}
								</Facet>
							);
						})}
					</div>
				) : null}
				{isMobile && isFacetsOpen ? (
					<button type="button" className="hawk-facet-rail__mobile-apply-btn" onClick={closeFacets}>
						Save
					</button>
				) : null}
			</div>
		</>
	);
}

export default FacetList;
